import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import { RFC2544SetupService } from '../../rfc2544-setup.service';
import {
  CommonLibModule,
  CommonRegEx,
  HelpersService,
  LicensesPageService,
  SchedulerFormat
} from '@nrwl/common-lib';
import { CommonModule } from '@angular/common';
import {
  BaseSetupTab,
  IComponentStateHistory,
  IValueChangedEvent,
  outputParams,
  REMOVED,
  TestSetupModule
} from '@nrwl/test-setup';
import { CommonMaterialModule } from '@nrwl/common-material';
import { FormsModule, NgForm } from '@angular/forms';
import {
  BACK_TO_BACK_TEST_TYPES,
  BANDWIDTH_ACCURACY,
  CBS_KILO_BYTE_SIZES,
  RFC2544ConfigFieldMapping as ConfigFieldMapping,
  CONFIRM_SELECTION,
  IPAddressValue,
  LAYER_TYPES,
  MAX_LINE_RATE_LIST,
  PERF_TEST_DURATIONS,
  STREAM_RATE_TYPE,
  TEST_MODE_TYPES
} from '../../models/models';
import { Subject, Subscription } from 'rxjs';
import { isNil } from 'lodash';
import { ScheduleLicense } from '@nrwl/licenses';

@Component({
  selector: 'nrwl-rfc2544-setup',
  standalone: true,
  imports: [CommonModule, TestSetupModule, CommonMaterialModule, FormsModule, CommonLibModule],
  templateUrl: './rfc2544-setup.component.html',
  styleUrls: ['./rfc2544-setup.component.scss']
})
export class RFC2544SetupComponent implements OnInit, OnDestroy {
  @Input() tab: BaseSetupTab;
  @Input() setupCtrl: any; //eslint-disable-line @typescript-eslint/no-explicit-any
  @Input() componentId: string;
  @Input() stateHistory?: IComponentStateHistory;
  @Input() otherTabStateHistory: IComponentStateHistory;
  // eslint-disable-next-line @typescript-eslint/ban-types
  @Input() restoreValues?: Function;
  // eslint-disable-next-line @typescript-eslint/ban-types
  @Input() restoreAgentBasedConfig?: Function;
  @Input() previousOutput: outputParams;
  // eslint-disable-next-line @angular-eslint/no-output-on-prefix
  @Output() onDataValueChange = new EventEmitter<IValueChangedEvent>();
  // eslint-disable-next-line @angular-eslint/no-output-on-prefix
  @Output() onOtherTabDataValueChange = new EventEmitter<any>();

  @ViewChild('setupForm', { static: true }) form: NgForm;
  validTestDurationRegex: RegExp = CommonRegEx.HH_MM_SS_REGEX;
  addingColonTwoCharRegex: RegExp = CommonRegEx.COLON_TWO_CHARACTORS;
  configMapping = ConfigFieldMapping;
  newTestMode = true;
  customerName = '';
  technicianName = '';
  contact = '';
  email = '';
  phone = '';
  technicianId = '';
  comments='';
  testName = 'RFC2544';
  editMode = false;
  cloneMode = false;
  testInfo: any;
  config: any;
  outputs: Array<string>;
  tabsubscription: Subscription;
  maxTestDurationDefault = 7200;
  scheduler: SchedulerFormat;
  stream: any;
  scheduleInfoData: SchedulerFormat = {
    repeatInterval: 0,
    timeStamp: '',
    endTime: '',
    isValid: true
  };
  schedulerEnds = {
    enabled: false,
    optionsList: PERF_TEST_DURATIONS
  };
  isDisabledScheduler = false;
  testModes = [TEST_MODE_TYPES[0]];
  layerTypes = LAYER_TYPES;
  ipAddressTypes = [];
  agentType = null;
  setupFormField = {
    testMode: null,
    layerToTest: null,
    ipAddrType: null,
    streamRateConvention: null,
    maxTestDuration: null,
    throughput: true,
    throughputDuration: null,
    throughputFrameLossTolerance: null,
    throughputStopOverallTestOnFailure: null,
    throughputStartingUtilization: null,
    throughputMinUtilization: null,
    throughputMaxUtilization: null,
    throughputBandwidth: null,
    frameLoss: null,
    frameLossDuration: null,
    frameLossMaxLossAllowed: null,
    frameLossRunThroughputFails: null,
    frameLossReduceUtilization: null,
    latency: true,
    latencyDuration: null,
    latencyNumOfTrials: null,
    targetLatency: null,
    targetFdv: null,
    latencyUtilization: null,
    backToBack: true,
    backToBackTestType: null,
    backToBackMaxLineRate: null,
    backToBackRepetitions: null,
    backToBackTargetBurstDuration: null,
    cbsCommittedBurstSize: null,
    cbsKiloByteSize: null,
    backToBackBurstResolution: null,
    backToBackMaxTimeReceiveBurst: null
  };
  streamRateTypes = STREAM_RATE_TYPE;
  throughputStopOveralTestTypes = CONFIRM_SELECTION;
  frameLossRunThroughputTestFailsTypes = CONFIRM_SELECTION;
  cbsKiloByteSizeTypes = CBS_KILO_BYTE_SIZES;
  backToBackTestTypes = BACK_TO_BACK_TEST_TYPES;
  maxLineRateList = MAX_LINE_RATE_LIST;
  bandwidthAccuracy = BANDWIDTH_ACCURACY;
  isAdvancedSetup: boolean;
  readonly unsubscribe: Subject<void> = new Subject();
  isB2bMaxLineRateValid: boolean;
  rfc2544ScheduleLicense = ScheduleLicense.RFC2544;

  constructor(
    private readonly licenseService: LicensesPageService,
    public setupService: RFC2544SetupService,
    private readonly helperService: HelpersService
  ) {}
  ngOnInit(): void {
    this.tab.form = this.form;
    this.initializeData();
    if (this.setupService.tabDataChangeEvents) {
      this.tabsubscription = this.setupService.tabDataChangeEvents.subscribe(event => {
        if (event === 0) {
          this.restoreFormValues();
        }
      });
    }
    this.licenseService.activeTestLicenseDetails.subscribe(licenseData => {
      if (licenseData?.length) {
        this.isDisabledScheduler = !licenseData.some(license =>
          this.rfc2544ScheduleLicense?.includes(license.featureName)
        );
      }
    });
  }

  initializeData(): void {
    this.restoreAgentBasedConfig();
    this.restoreFormValues();
    this.newTestMode = this.setupService.mode === 'new';
    this.editMode = this.setupService.editMode;
    this.cloneMode = this.setupService.mode === 'clone';
    if (!this.newTestMode) {
      this.onTestModeChange();
    }
  }

  restoreFormValues(): void {
    this.tab.form = this.form;
    this.config = this.setupService.currentTab.config;
    this.outputs = this.config.def.outputs;
    this.ipAddressTypes = this.config.options[ConfigFieldMapping.IP_ADDRESS_TYPE].value;
    this.testName = this.restoreFieldValues(ConfigFieldMapping.ALIAS);
    this.customerName = this.restoreFieldValues(ConfigFieldMapping.CUSTOMER);
    this.technicianName = this.restoreFieldValues(ConfigFieldMapping.TECHINICIAN);
    this.contact = this.restoreFieldValues(ConfigFieldMapping.CONTACT);
    this.email = this.restoreFieldValues(ConfigFieldMapping.EMAIL);
    this.phone = this.restoreFieldValues(ConfigFieldMapping.PHONE);
    this.technicianId = this.restoreFieldValues(ConfigFieldMapping.TECHINICIANID);
    this.comments = this.restoreFieldValues(ConfigFieldMapping.COMMENTS);
    this.agentType = this.restoreFieldValues(ConfigFieldMapping.AGENT_TYPE);
    this.setupFormField.testMode = this.restoreFieldValues(ConfigFieldMapping.TEST_MODE);
    this.setupFormField.layerToTest = this.restoreFieldValues(ConfigFieldMapping.LAYER_TO_TEST);
    this.setupFormField.ipAddrType = this.restoreFieldValues(ConfigFieldMapping.IP_ADDRESS_TYPE);
    this.setupFormField.streamRateConvention = this.restoreFieldValues(
      ConfigFieldMapping.STREAM_RATE_CONVENTION
    );
    this.setupFormField.maxTestDuration = this.helperService.convertSecondsToHMS(
      this.restoreFieldValues(ConfigFieldMapping.MAX_TEST_DURATION)
    );
    this.setupFormField.throughput = this.restoreFieldValues(ConfigFieldMapping.THROUGHPUT);
    this.setupFormField.throughputDuration = this.restoreFieldValues(
      ConfigFieldMapping.THROUGHPUT_DURATION
    );
    this.setupFormField.throughputFrameLossTolerance = this.restoreFieldValues(
      ConfigFieldMapping.THROUGHPUT_FRAME_LOSS_TOLERANCE
    );
    this.setupFormField.throughputStopOverallTestOnFailure = this.restoreFieldValues(
      ConfigFieldMapping.THROUGHTPUT_STOP_OVERALL_TEST_ON_FAILURE
    );
    this.setupFormField.throughputStartingUtilization = this.restoreFieldValues(
      ConfigFieldMapping.THROUGHPUT_STARTING_UTILIZATION
    );
    this.setupFormField.throughputMinUtilization = this.restoreFieldValues(
      ConfigFieldMapping.THROUGHPUT_MIN_UTILIZATION
    );
    this.setupFormField.throughputMaxUtilization = this.restoreFieldValues(
      ConfigFieldMapping.THROUGHPUT_MAX_UTILIZATION
    );
    this.setupFormField.throughputBandwidth = this.restoreFieldValues(
      ConfigFieldMapping.THROUGHPUT_BANDWIDTH
    );
    this.setupFormField.frameLoss = this.restoreFieldValues(ConfigFieldMapping.FRAME_LOSS);
    this.setupFormField.frameLossDuration = this.restoreFieldValues(
      ConfigFieldMapping.FRAME_LOSS_DURATION
    );
    this.setupFormField.frameLossMaxLossAllowed = this.restoreFieldValues(
      ConfigFieldMapping.FRAME_LOSS_MAX_LOSS_ALLOWED
    );
    this.setupFormField.frameLossRunThroughputFails = this.restoreFieldValues(
      ConfigFieldMapping.FRAME_LOSS_RUN_THORUGHPUT_FAILS
    );
    this.setupFormField.frameLossReduceUtilization = this.restoreFieldValues(
      ConfigFieldMapping.FRAME_LOSS_REDUCE_UTILIZATION
    );
    this.setupFormField.latency = this.restoreFieldValues(ConfigFieldMapping.LATENCY);
    this.setupFormField.latencyDuration = this.restoreFieldValues(
      ConfigFieldMapping.LATENCY_DURATION
    );
    this.setupFormField.latencyNumOfTrials = this.restoreFieldValues(
      ConfigFieldMapping.LATENCY_NUM_OF_TRIALS
    );
    this.setupFormField.targetLatency = this.restoreFieldValues(ConfigFieldMapping.TARGET_LATENCY);
    this.setupFormField.targetFdv = this.restoreFieldValues(ConfigFieldMapping.TARGET_FDV);
    this.setupFormField.latencyUtilization = this.restoreFieldValues(
      ConfigFieldMapping.LATENCY_UTILIZATION
    );
    this.setupFormField.backToBack = this.restoreFieldValues(ConfigFieldMapping.BACK_TO_BACK);
    this.setupFormField.backToBackTestType = this.restoreFieldValues(
      ConfigFieldMapping.BACK_TO_BACK_TEST_TYPE
    );
    this.setupFormField.backToBackMaxLineRate = this.restoreFieldValues(
      ConfigFieldMapping.BACK_TO_BACK_MAX_LINE_RATE
    );
    this.setupFormField.backToBackRepetitions = this.restoreFieldValues(
      ConfigFieldMapping.BACK_TO_BACK_REPETITIONS
    );
    this.setupFormField.backToBackTargetBurstDuration = this.restoreFieldValues(
      ConfigFieldMapping.BACK_TO_BACK_TARGET_BURST_DURATION
    );
    this.setupFormField.cbsCommittedBurstSize = this.restoreFieldValues(
      ConfigFieldMapping.CBS_COMMITTED_BURST_SIZE
    );
    this.setupFormField.cbsKiloByteSize = this.restoreFieldValues(
      ConfigFieldMapping.CBS_KILO_BYTE_SIZE
    );
    this.setupFormField.backToBackBurstResolution = this.restoreFieldValues(
      ConfigFieldMapping.BACK_TO_BACK_BURST_RESOLUTION
    );
    this.setupFormField.backToBackMaxTimeReceiveBurst = this.restoreFieldValues(
      ConfigFieldMapping.BACK_TO_BACK_MAX_TIME_RECEIVE_BURST
    );
    this.restoreScheduleParams(ConfigFieldMapping.SCHEDULER);
    this.restoreOtherTabValues();
  }

  restoreOtherTabValues(): void {
    this.stream = this.restoreAdvancedFieldValues(ConfigFieldMapping.STREAM);
    if (
      this.stream &&
      this.setupFormField?.backToBackMaxLineRate < this.stream[ConfigFieldMapping.CIR]
    ) {
      this.isB2bMaxLineRateValid = true;
      this.onDataEmit(
        ConfigFieldMapping.BACK_TO_BACK_MAX_LINE_RATE,
        this.setupFormField.backToBackMaxLineRate,
        false
      );
    }
  }

  //eslint-disable-next-line
  restoreAdvancedFieldValues(field: string): any {
    const data = this.otherTabStateHistory('rfc2544.streams.html', field);
    if (data && !isNil(data.value) && data.value !== REMOVED) {
      return data.value;
    }
    return null;
  }

  restoreScheduleParams(field: string): void {
    const data = this.stateHistory(this.componentId, field);
    if (data && data.value !== REMOVED) {
      this.populateScheduler(data.value);
    }
  }

  populateScheduler(schedulerInfo: SchedulerFormat): void {
    this.scheduleInfoData.repeatInterval = schedulerInfo.repeatInterval;
    this.scheduleInfoData.timeStamp = schedulerInfo.timeStamp;
    this.scheduleInfoData.isValid = true;
    this.emitScedulerParams();
  }

  restoreFieldValues(field: string): any {
    const data = this.stateHistory(this.componentId, field);
    if (data && !isNil(data.value) && data.value !== REMOVED) {
      return data.value;
    }
    return null;
  }

  onTestModeChange(): void {
    this.onDataEmit(ConfigFieldMapping.TEST_MODE, this.setupFormField.testMode, true);
    this.restoreAgentBasedConfig(this.agentType, this.setupFormField.testMode);
    this.restoreFormValues();
  }

  updateTestIdentity(event: {
    testName: string;
    customer: string;
    technician: string;
    contact: string;
    email: string;
    phone: string;
    technicianId: string;
    isValidName: boolean;
    comments: string;
  }): void {
    this.onDataEmit('alias', event.testName, event.isValidName);
    this.onDataEmit('customer', event.customer, true);
    this.onDataEmit('technician', event.technician, true);
    this.onDataEmit('contact', event.contact, true);
    this.onDataEmit('email', event.email, true);
    this.onDataEmit('phone', event.phone, true);
    this.onDataEmit('technicianId', event.technicianId, true);
    this.onDataEmit('comments',event.comments,true);
    const controls = this.form?.form?.controls;
    const allValid = controls ? Object.values(controls).every(control => !control.invalid) : true;
    const isFormValid = event.isValidName && allValid;
    this.form.form?.setErrors(isFormValid ? null : { invalid: true });
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  onDataEmit(key: string, newValue: any, isValid: boolean): void {
    this.onDataValueChange.emit({
      componentId: this.componentId,
      key: key,
      newValue: newValue,
      isValid: isValid
    });
  }

  onLayerChange(): void {
    this.setupFormField.ipAddrType = IPAddressValue.IP_V4;
    this.ipAddressTypes = this.config.options[ConfigFieldMapping.IP_ADDRESS_TYPE].value;
    this.onDataEmit(ConfigFieldMapping.LAYER_TO_TEST, this.setupFormField.layerToTest, true);
  }

  onIpAddressTypeChange(): void {
    this.onDataEmit(ConfigFieldMapping.IP_ADDRESS_TYPE, this.setupFormField.ipAddrType, true);
  }

  onStreamRateConventionChange(): void {
    this.onDataEmit(
      ConfigFieldMapping.STREAM_RATE_CONVENTION,
      this.setupFormField.streamRateConvention,
      true
    );
  }

  maxTestDurationChange(): void {
    let duration = null;
    duration = this.getTimeDurationInSeconds();
    this.onDataEmit(ConfigFieldMapping.MAX_TEST_DURATION, duration, true);
  }

  getTimeDurationInSeconds(): number {
    let duration = this.maxTestDurationDefault;
    const hms = this.setupFormField.maxTestDuration.split(':');
    if (hms.length === 3) {
      const hours = parseInt(hms[0], 10);
      const minutes = parseInt(hms[1], 10);
      const seconds = parseInt(hms[2], 10);
      duration = Number(hours) * 60 * 60 + Number(minutes) * 60 + seconds;
    }
    return duration;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  reformatTestDuration(event: any) {
    if (event.data && event.target.value.length < 8) {
      event.target.value = event.target.value.replace(this.addingColonTwoCharRegex, '$1:');
    }
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  throughputToggleChange($event: any): void {
    this.setupFormField.throughput = $event.checked;
    this.onDataEmit(ConfigFieldMapping.THROUGHPUT, this.setupFormField.throughput, true);
  }

  throughputDurationChange(): void {
    this.onDataEmit(
      ConfigFieldMapping.THROUGHPUT_DURATION,
      this.setupFormField.throughputDuration,
      true
    );
  }

  throughputFrameLossToleranceChange(): void {
    this.onDataEmit(
      ConfigFieldMapping.THROUGHPUT_FRAME_LOSS_TOLERANCE,
      this.setupFormField.throughputFrameLossTolerance,
      true
    );
  }

  throughputStopOverallTestOnFailureChange(): void {
    this.onDataEmit(
      ConfigFieldMapping.THROUGHTPUT_STOP_OVERALL_TEST_ON_FAILURE,
      this.setupFormField.throughputStopOverallTestOnFailure,
      true
    );
  }

  throughputStartingUtilizationChange(): void {
    this.onDataEmit(
      ConfigFieldMapping.THROUGHPUT_STARTING_UTILIZATION,
      this.setupFormField.throughputStartingUtilization,
      true
    );
  }

  throughputMinUtilizationChange(): void {
    this.onDataEmit(
      ConfigFieldMapping.THROUGHPUT_MIN_UTILIZATION,
      this.setupFormField.throughputMinUtilization,
      true
    );
  }

  throughputMaxUtilizationChange(): void {
    this.onDataEmit(
      ConfigFieldMapping.THROUGHPUT_MAX_UTILIZATION,
      this.setupFormField.throughputMaxUtilization,
      true
    );
  }

  throughputBandwidthChange(): void {
    this.onDataEmit(
      ConfigFieldMapping.THROUGHPUT_BANDWIDTH,
      this.setupFormField.throughputBandwidth,
      true
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  frameLossToggleChange($event: any): void {
    this.setupFormField.frameLoss = $event.checked;
    this.onDataEmit(ConfigFieldMapping.FRAME_LOSS, this.setupFormField.frameLoss, true);
  }

  frameLossDurationChange(): void {
    this.onDataEmit(
      ConfigFieldMapping.FRAME_LOSS_DURATION,
      this.setupFormField.frameLossDuration,
      true
    );
  }

  frameLossMaxLossAllowedChange(): void {
    this.onDataEmit(
      ConfigFieldMapping.FRAME_LOSS_MAX_LOSS_ALLOWED,
      this.setupFormField.frameLossMaxLossAllowed,
      true
    );
  }

  frameLossRunThroughputFailsChange(): void {
    this.onDataEmit(
      ConfigFieldMapping.FRAME_LOSS_RUN_THORUGHPUT_FAILS,
      this.setupFormField.frameLossRunThroughputFails,
      true
    );
  }

  frameLossReduceUtilizationChange(): void {
    this.onDataEmit(
      ConfigFieldMapping.FRAME_LOSS_REDUCE_UTILIZATION,
      this.setupFormField.frameLossReduceUtilization,
      true
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  latencyToggleChange($event: any): void {
    this.setupFormField.latency = $event.checked;
    this.onDataEmit(ConfigFieldMapping.LATENCY, this.setupFormField.latency, true);
  }

  latencyDurationChange(): void {
    this.onDataEmit(ConfigFieldMapping.LATENCY_DURATION, this.setupFormField.latencyDuration, true);
  }

  latencyNumOfTrialsChange(): void {
    this.onDataEmit(
      ConfigFieldMapping.LATENCY_NUM_OF_TRIALS,
      this.setupFormField.latencyNumOfTrials,
      true
    );
  }

  targetLatencyChange(): void {
    this.onDataEmit(ConfigFieldMapping.TARGET_LATENCY, this.setupFormField.targetLatency, true);
  }

  targetFdvChange(): void {
    this.onDataEmit(ConfigFieldMapping.TARGET_FDV, this.setupFormField.targetFdv, true);
  }

  latencyUtilizationChange(): void {
    this.onDataEmit(
      ConfigFieldMapping.LATENCY_UTILIZATION,
      this.setupFormField.latencyUtilization,
      true
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  backToBackToggleChange($event): void {
    this.setupFormField.backToBack = $event.checked;
    this.onDataEmit(ConfigFieldMapping.BACK_TO_BACK, this.setupFormField.backToBack, true);
  }

  backToBackTestTypeChange(): void {
    this.onDataEmit(
      ConfigFieldMapping.BACK_TO_BACK_TEST_TYPE,
      this.setupFormField.backToBackTestType,
      true
    );
  }

  backToBackMaxLineRateChange(): void {
    if (this.stream && !isNil(this.stream['cir-from-engine-a'])) {
      if (this.setupFormField?.backToBackMaxLineRate < this.stream['cir-from-engine-a']) {
        this.isB2bMaxLineRateValid = true;
        this.onDataEmit(
          ConfigFieldMapping.BACK_TO_BACK_MAX_LINE_RATE,
          this.setupFormField.backToBackMaxLineRate,
          false
        );
      } else {
        this.isB2bMaxLineRateValid = false;
        this.onDataEmit(
          ConfigFieldMapping.BACK_TO_BACK_MAX_LINE_RATE,
          this.setupFormField.backToBackMaxLineRate,
          true
        );
      }
    } else {
      this.onDataEmit(
        ConfigFieldMapping.BACK_TO_BACK_MAX_LINE_RATE,
        this.setupFormField.backToBackMaxLineRate,
        true
      );
    }
  }

  backToBackRepetitionsChange(): void {
    this.onDataEmit(
      ConfigFieldMapping.BACK_TO_BACK_REPETITIONS,
      this.setupFormField.backToBackRepetitions,
      true
    );
  }

  backToBackTargetBurstDurationChange(): void {
    this.onDataEmit(
      ConfigFieldMapping.BACK_TO_BACK_TARGET_BURST_DURATION,
      this.setupFormField.backToBackTargetBurstDuration,
      true
    );
  }

  cbsCommittedBurstSizeChange(): void {
    this.onDataEmit(
      ConfigFieldMapping.CBS_COMMITTED_BURST_SIZE,
      this.setupFormField.cbsCommittedBurstSize,
      true
    );
  }

  cbsKiloByteSizeChange(): void {
    this.onDataEmit(
      ConfigFieldMapping.CBS_KILO_BYTE_SIZE,
      this.setupFormField.cbsKiloByteSize,
      true
    );
  }

  backToBackBurstResolutionChange(): void {
    this.onDataEmit(
      ConfigFieldMapping.BACK_TO_BACK_BURST_RESOLUTION,
      this.setupFormField.backToBackBurstResolution,
      true
    );
  }

  backToBackMaxTimeReceiveBurstChange(): void {
    this.onDataEmit(
      ConfigFieldMapping.BACK_TO_BACK_MAX_TIME_RECEIVE_BURST,
      this.setupFormField.backToBackMaxTimeReceiveBurst,
      true
    );
  }

  onChangeTestSetup(): void {
    this.isAdvancedSetup = !this.isAdvancedSetup;
  }

  fetchSchedulerVal(event: SchedulerFormat): void {
    this.scheduler = event;
    this.scheduleInfoData.repeatInterval = event.repeatInterval;
    this.scheduleInfoData.timeStamp = this.setupService.getScheduleTimeStamp(event.timeStamp);
    this.scheduleInfoData.isValid = event.isValid;
    this.scheduleInfoData.endTime = event.endTime;
    if (this.scheduleInfoData.endTime) {
      this.onDataEmit(ConfigFieldMapping.PERF_TEST_DURATION, this.scheduleInfoData.endTime, true);
    }
    this.emitScedulerParams();
  }

  emitScedulerParams(): void {
    this.onDataEmit(
      ConfigFieldMapping.SCHEDULER,
      this.scheduleInfoData,
      this.scheduleInfoData.isValid
    );
  }

  ngOnDestroy(): void {
    this.unsubscribe.complete();
    this.unsubscribe.next();
    if (this.tabsubscription) {
      this.tabsubscription.unsubscribe();
    }
  }
}
